import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { margin, marginSizes } from 'theme/default'
import { createArray } from 'tools/array'
import { isSameDate, getCurrentYear, getTranslatedMonths } from 'tools/date'
import useControlledInput from 'hooks/useControlledInput'
import Component from 'decorators/Component'
import SelectWithLabel from 'components/Select/SelectWithLabel'
import Flex from 'components/Flex'
import useDatePicker from './useDatePicker'

const StyledSelectWithLabel = styled(SelectWithLabel)`
  margin-right: ${margin[marginSizes.small]};
  margin-left: ${margin[marginSizes.small]};
`

const isSameDateValue = (d1, d2) => {
  if (d1 instanceof Date && d2 instanceof Date) {
    return isSameDate(d1, d2)
  }
  return d1 === d2
}

const DropdownDatePicker = ({
  t,
  name,
  selectedDate = null,
  onChange,
  controlled,
  withError,
  toYear = getCurrentYear(),
  fromYear = getCurrentYear() - 100,
  dayLabel = t('Day'),
  monthLabel = t('Month'),
  yearLabel = t('Year')
}) => {
  const [date, onDateChange] = useControlledInput({ name, value: selectedDate, onChange, controlled, isSameValue: isSameDateValue })

  const { state, setDay, setMonth, setYear, daysInMonth, reset } = useDatePicker(date, onDateChange)

  React.useEffect(() => {
    if (state.date instanceof Date) {
      const year = state.date.getFullYear()
      if (year < fromYear || year > toYear) {
        reset() // Reset state if date is out of range
      }
    }
  }, [state.date, fromYear, toYear, reset])

  const dayOptions = React.useMemo(() => {
    const days = createArray(daysInMonth).map((_, index) => ({ label: index + 1, value: index + 1 }))
    return [{ label: dayLabel, disabled: true }, ...days]
  }, [dayLabel, daysInMonth])

  const monthOptions = React.useMemo(() => {
    const months = getTranslatedMonths(t, 'short').map((month, index) => ({ label: month, value: index }))
    return [{ label: monthLabel, disabled: true }, ...months]
  }, [monthLabel, t])

  const yearOptions = React.useMemo(() => {
    const years = createArray(toYear - fromYear + 1).map(index => ({ label: toYear - index, value: toYear - index }))
    return [{ label: yearLabel, disabled: true }, ...years]
  }, [fromYear, toYear, yearLabel])

  const onDayChange = React.useCallback((_, { value }) => setDay(value), [setDay])
  const onMonthChange = React.useCallback((_, { value }) => setMonth(value), [setMonth])
  const onYearChange = React.useCallback((_, { value }) => setYear(value), [setYear])

  const selectedDay = React.useMemo(() => {
    return dayOptions.find(({ value }) => value === state.day) || {}
  }, [dayOptions, state.day])

  const selectedMonth = React.useMemo(() => {
    return monthOptions.find(({ value }) => value === state.month) || {}
  }, [monthOptions, state.month])

  const selectedYear = React.useMemo(() => {
    return yearOptions.find(({ value }) => value === state.year) || {}
  }, [yearOptions, state.year])

  return (
    <Flex>
      <SelectWithLabel name='day' withError={withError} label={dayLabel} onChange={onDayChange} options={dayOptions} selectedOption={selectedDay} controlled />
      <StyledSelectWithLabel name='month' withError={withError} label={monthLabel} onChange={onMonthChange} options={monthOptions} selectedOption={selectedMonth} controlled />
      <SelectWithLabel name='year' withError={withError} label={yearLabel} onChange={onYearChange} options={yearOptions} selectedOption={selectedYear} controlled />
    </Flex>
  )
}

DropdownDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  fromYear: PropTypes.number,
  toYear: PropTypes.number,
  dayLabel: PropTypes.string,
  monthLabel: PropTypes.string,
  yearLabel: PropTypes.string,
  withError: PropTypes.bool,
  controlled: PropTypes.bool,
  selectedDate: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string
  ])
}

export default Component()(DropdownDatePicker)
